import axios from "axios";
import config from "../environments/config";
import Cookies from "js-cookie";

let instance = axios.create({
  baseURL: config.apiUrl,
  headers: {
    "Content-type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
    "Access-Control-Allow-Origin": "*",
    Authorization: Cookies.get("user")
      ? `Bearer ${JSON.parse(Cookies.get("user")).token}`
      : "",
  },
});

// Shared by both interceptors below: whatever the trigger (proactively-detected expiry,
// or a 401 the server sent back for any other reason — revoked session, clock skew beyond
// the grace window, a cleared cookie, ...), the cleanup + redirect is the same.
function redirectToLogin() {
  Cookies.remove("user");
  Cookies.remove("oktaConfig");
  // Clear the activity timestamp so the next login doesn't inherit a stale value and get
  // logged out immediately (#1817 #1818)
  localStorage.removeItem("lastActivityTimestamp");
  window.location.href = "/login";
}

// If JWT is expired, clear the cookie and redirect to login
instance.interceptors.request.use(
  (config) => {
    // Get token for validation
    const userCookie = Cookies.get("user");
    const token = userCookie ? JSON.parse(userCookie).token : null;

    if (token) {
      // Decode token
      const payload = JSON.parse(atob(token.split(".")[1]));

      // Check if auth header needs to be updated
      const hasExistingAuth = config.headers.Authorization && config.headers.Authorization !== "";
      if (!hasExistingAuth) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Check token expiration, with a small grace period so minor clock
      // skew between the client and the API can't make a freshly issued
      // token look expired
      const CLOCK_SKEW_GRACE_SECONDS = 30;
      const expiration = payload.exp;
      const currentTimestamp = Math.floor(Date.now() / 1000);
      if (expiration + CLOCK_SKEW_GRACE_SECONDS < currentTimestamp) {
        redirectToLogin();
        return Promise.reject("Token expired");
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// The request interceptor above only catches expiry the client can predict ahead of time
// from the token's own `exp` claim. Any 401 that comes back from the server for another
// reason (revoked session, clock skew beyond the grace window, a cookie that got cleared,
// ...) previously just propagated as an ordinary failed request, with nothing telling the
// user their session had ended — this catches those too.
//
// #2583: a handful of non-critical, side-effect lookups (e.g. populating a dropdown) hit
// endpoints whose backend authorization can fail with a 401 for a reason that has nothing
// to do with the caller's session being valid — a permission problem, not an expired/invalid
// token. Treating that the same as a dead session force-logs out a user who was never actually
// logged out. Callers can opt a specific request out of the forced redirect via
// `{ skipAuthRedirect: true }` in its config; any OTHER request (including the very next one
// that request's own caller makes) still catches a truly-dead session normally.
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);

export const http = instance;
